// Projectile primitive — animated tracer that arcs from origin to target
// over durationSec. Used for arrows, bolts, magical projectiles.
//
// The arc height scales with the horizontal distance so short shots stay
// flat and long shots visibly arc. The mesh is oriented along its velocity
// each frame so the tracer reads as flying in the right direction.

import { useFrame } from '@react-three/fiber';
import { useMemo, useRef } from 'react';
import * as THREE from 'three';

interface ProjectileProps {
  readonly origin: readonly [number, number, number];
  readonly target: readonly [number, number, number];
  readonly durationSec: number;
  readonly color: THREE.ColorRepresentation;
  readonly size?: number;
  readonly arcHeight?: number;
  readonly onComplete?: () => void;
}

export function Projectile({
  origin,
  target,
  durationSec,
  color,
  size = 0.4,
  arcHeight,
  onComplete,
}: ProjectileProps) {
  const meshRef = useRef<THREE.Mesh>(null);
  const startTimeRef = useRef(performance.now());
  const doneRef = useRef(false);

  const originVec = useMemo(() => new THREE.Vector3(...origin), [origin]);
  const targetVec = useMemo(() => new THREE.Vector3(...target), [target]);
  const horizDist = useMemo(() => {
    const dx = target[0] - origin[0];
    const dz = target[2] - origin[2];
    return Math.sqrt(dx * dx + dz * dz);
  }, [origin, target]);
  // Default arc grows with distance, capped at 1.0 world unit.
  const arc = arcHeight ?? Math.min(horizDist * 0.15, 1.0);

  const upVec = useMemo(() => new THREE.Vector3(0, 1, 0), []);

  useFrame(() => {
    const mesh = meshRef.current;
    if (!mesh) return;
    const elapsed = (performance.now() - startTimeRef.current) / 1000;
    const t = Math.min(elapsed / durationSec, 1);

    if (t >= 1) {
      // Gate with a ref so onComplete fires exactly once. useFrame keeps
      // ticking until React unmounts us; without this guard we'd re-invoke
      // the parent's setActive filter on every frame between expiry and
      // unmount.
      if (!doneRef.current) {
        doneRef.current = true;
        onComplete?.();
      }
      return;
    }

    const x = origin[0] + (target[0] - origin[0]) * t;
    const z = origin[2] + (target[2] - origin[2]) * t;
    const baseY = origin[1] + (target[1] - origin[1]) * t;
    const y = baseY + Math.sin(t * Math.PI) * arc;
    mesh.position.set(x, y, z);

    // Orient cylinder (+Y by default) along the instantaneous velocity.
    // Approximate velocity numerically by sampling t+epsilon.
    const e = 0.01;
    const t2 = Math.min(t + e, 1);
    const x2 = origin[0] + (target[0] - origin[0]) * t2;
    const z2 = origin[2] + (target[2] - origin[2]) * t2;
    const baseY2 = origin[1] + (target[1] - origin[1]) * t2;
    const y2 = baseY2 + Math.sin(t2 * Math.PI) * arc;
    const dir = new THREE.Vector3(x2 - x, y2 - y, z2 - z).normalize();
    const quat = new THREE.Quaternion().setFromUnitVectors(upVec, dir);
    mesh.quaternion.copy(quat);
  });

  return (
    <mesh ref={meshRef} position={originVec}>
      <cylinderGeometry args={[size * 0.08, size * 0.08, size, 6]} />
      <meshStandardMaterial color={color} emissive={color} emissiveIntensity={0.6} />
    </mesh>
  );
}
